import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useCRMData } from '../context/CRMDataContext';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, getActivityColor, formatDate } from '../utils/helpers';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import BookMeetingModal from '../components/modals/BookMeetingModal';
import ActivityForm from '../components/forms/ActivityForm';
import BaseModal from '../components/modals/BaseModal';

const DASHBOARD_ACTIVITY_MODAL_EXIT_DELAY = 220;

const getActivityCompanyName = (activity, companies = [], deals = []) => {
  const directCompanyId = activity?.company_id || activity?.companyId;
  const relatedDealId = activity?.deal_id || activity?.dealId;
  const relatedDeal = deals.find(deal => (
    deal?.deal_id === relatedDealId || deal?.id === relatedDealId
  ));
  const companyId = directCompanyId || relatedDeal?.company_id || relatedDeal?.companyId;

  return companies.find(company => (
    company?.company_id === companyId || company?.id === companyId
  ))?.name || '';
};

const getActivityDisplayTitle = (activity, companies = [], deals = []) => {
  const title = activity?.title || activity?.name || 'Aktivitet';
  const companyName = getActivityCompanyName(activity, companies, deals);

  return companyName ? `${companyName} – ${title}` : title;
};

const getDealStatus = deal => String(deal?.status || '').toLowerCase();

const isWonDeal = deal => (
  getDealStatus(deal) === 'vunnet' ||
  getDealStatus(deal) === 'customer' ||
  getDealStatus(deal) === 'closed_won' ||
  deal?.isCustomer === true ||
  deal?.convertedToCustomer === true ||
  String(deal?.customerStatus || '').toLowerCase() === 'active'
);

const isActiveDeal = (deal) => {
  const status = getDealStatus(deal);

  return (
    Boolean(deal) &&
    deal.archived !== true &&
    deal.deleted !== true &&
    deal.is_deleted !== true &&
    status !== 'arkivert' &&
    status !== 'slettet' &&
    status !== 'deleted' &&
    !isWonDeal(deal)
  );
};

const getDealValue = deal => (
  Number(deal?.value || deal?.dealValue || deal?.amount || 0)
);

const hasPipelineStage = (deal) => Boolean(deal?.stage_id);

const getDashboardActivityKey = (activity) => {
  if (activity?.id) return `dashboard-activity-${activity.id}`;
  if (activity?.activity_id) return `dashboard-activity-${activity.activity_id}`;

  return `dashboard-activity-${[
    activity?.title || 'untitled',
    activity?.date || 'no-date',
    activity?.type || 'unknown'
  ].join('-')}`;
};

const getDashboardActivityPlacementKey = (activity, placement) => {
  return `${getDashboardActivityKey(activity)}-${placement}`;
};

const MemoizedDashboardActivityCard = React.memo(({
  activity,
  onOpenActivity,
  onMarkDone,
  companyName,
  companies,
  deals,
  isOverdue,
  variant,
  placement
}) => {
  const displayTitle = getActivityDisplayTitle(activity, companies, deals);

  if (variant === 'timeline') {
    const colors = getActivityColor(activity.type);

    return (
      <div onClick={() => onOpenActivity(activity)} className="group relative pl-8 py-3 cursor-pointer hover:bg-slate-50/50 rounded-r-xl">
        <div className={`absolute left-0 top-5 w-3 h-3 rounded-full border-2 border-white shadow-sm ${colors.dot} z-10`}></div>
        <div className="bg-slate-50 border border-slate-200 p-3 rounded-xl group-hover:bg-white group-hover:shadow-md group-hover:border-primary-200">
          <div className="flex justify-between items-start">
            <div className="min-w-0">
              <span className="text-xs font-black text-slate-800 line-clamp-1">{displayTitle}</span>
            </div>
            <span className="text-[9px] font-bold text-slate-400 whitespace-nowrap">
              {new Date(activity.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-[9px] font-bold uppercase tracking-wider ${colors.text}`}>{activity.type}</span>
            {activity.company_id && (
              <span className="text-[9px] text-slate-400 truncate max-w-[100px]">
                • {companyName || 'Ukjent'}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-3 p-3 hover:bg-slate-50 rounded-xl cursor-pointer">
      <button onClick={(e) => { e.stopPropagation(); onMarkDone(activity.activity_id); }} className="w-5 h-5 rounded border-2 border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 flex items-center justify-center flex-shrink-0">
        <SafeIcon icon="✓" className="w-3 h-3 text-emerald-600 opacity-0 hover:opacity-100" />
      </button>
      <div className="flex-1 min-w-0" onClick={() => onOpenActivity(activity)}>
        <p className={`text-sm font-bold truncate ${isOverdue ? 'text-rose-600' : 'text-slate-700'}`}>
          {displayTitle}
        </p>
        <p className="text-[10px] text-slate-400 font-medium truncate">
          {companyName || 'Ingen firma'}
        </p>
      </div>
      <div className="text-right">
        <p className={`text-[9px] font-bold uppercase tracking-wider ${isOverdue ? 'text-rose-500' : 'text-slate-400'}`}>
          {isOverdue ? 'Forfalt' : formatDate(activity.date)}
        </p>
      </div>
    </div>
  );
});

const Dashboard = () => {
  const { deals, activities, companies, updateActivity } = useCRMData();
  const { currentUser } = useAuth();
  const sentContracts = [];

  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const activityModalCleanupRef = useRef(null);

  const clearActivityModalCleanup = useCallback(() => {
    if (activityModalCleanupRef.current) {
      clearTimeout(activityModalCleanupRef.current);
      activityModalCleanupRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearActivityModalCleanup();
    };
  }, [clearActivityModalCleanup]);

  const pipelineStats = useMemo(() => {
    const safeDeals = Array.isArray(deals) ? deals : [];
    const activeDeals = safeDeals.filter(isActiveDeal);
    const openDeals = activeDeals.filter(d => d.status === 'Åpen' && hasPipelineStage(d));
    const wonDeals = safeDeals.filter(d => !d?.archived && !d?.deleted && !d?.is_deleted && isWonDeal(d));

    return {
      count: openDeals.length,
      value: openDeals.reduce((sum, d) => sum + (Number(d.value) || 0), 0),
      wonValue: wonDeals.reduce((sum, d) => sum + getDealValue(d), 0),
      generated: activeDeals.length
    };
  }, [deals]);

  const documentStats = useMemo(() => {
    const safeContracts = sentContracts || [];
    const sentValue = safeContracts
      .filter(c => c.status === 'sent')
      .reduce((sum, c) => sum + (Number(c.value) || 0), 0);
    const viewedValue = safeContracts
      .filter(c => c.status === 'viewed')
      .reduce((sum, c) => sum + (Number(c.value) || 0), 0);
    const signedValue = safeContracts
      .filter(c => c.status === 'signed')
      .reduce((sum, c) => sum + (Number(c.value) || 0), 0);

    return { sentValue, viewedValue, signedValue };
  }, [sentContracts]);

  const activityBuckets = useMemo(() => {
    const safeActivities = activities || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const overdue = [];
    const todays = [];
    const tomorrows = [];
    const upcoming = [];

    safeActivities.forEach(act => {
      if (act.status === 'Gjennomført') return;

      const actDate = new Date(act.date);
      actDate.setHours(0, 0, 0, 0);

      if (actDate < today) {
        overdue.push(act);
      } else if (actDate.getTime() === today.getTime()) {
        todays.push(act);
      } else if (actDate.getTime() === tomorrow.getTime()) {
        tomorrows.push(act);
      } else {
        upcoming.push(act);
      }
    });

    upcoming.sort((a, b) => new Date(a.date) - new Date(b.date));

    return { overdue, todays, tomorrows, upcoming };
  }, [activities]);

  const openActivityModal = useCallback((activity) => {
    clearActivityModalCleanup();
    setSelectedActivity(activity);
    setIsActivityModalOpen(true);
  }, [clearActivityModalCleanup]);

  const closeActivityModal = useCallback(() => {
    setIsActivityModalOpen(false);
    clearActivityModalCleanup();

    activityModalCleanupRef.current = setTimeout(() => {
      setSelectedActivity(null);
      activityModalCleanupRef.current = null;
    }, DASHBOARD_ACTIVITY_MODAL_EXIT_DELAY);
  }, [clearActivityModalCleanup]);

  const handleEditActivity = useCallback((activity) => {
    openActivityModal(activity);
  }, [openActivityModal]);

  const handleMarkDone = useCallback((id) => {
    updateActivity(id, { status: 'Gjennomført' });
  }, [updateActivity]);

  const renderTimelineItem = (activity) => (
    <MemoizedDashboardActivityCard
      key={getDashboardActivityPlacementKey(activity, 'agenda')}
      activity={activity}
      onOpenActivity={handleEditActivity}
      companyName={getActivityCompanyName(activity, companies, deals)}
      companies={companies}
      deals={deals}
      variant="timeline"
      placement="agenda"
    />
  );

  const renderEmptyState = (text) => (
    <div className="py-4 pl-8">
      <div className="bg-slate-50/50 border border-slate-100 border-dashed rounded-xl p-3 text-center">
        <p className="text-xs font-medium text-slate-400">{text}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="relative h-[420px] w-full bg-gradient-to-br from-[#1E3A8A] via-[#2563EB] to-[#4F46E5] overflow-hidden flex flex-col items-center pt-16">
        <img src="https://images.unsplash.com/photo-1506459225024-1428097a7e18?q=80&w=2500&auto=format&fit=crop" className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-overlay" alt="Dashboard Header" />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/25 via-blue-900/40 to-slate-50"></div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <p className="text-blue-100 text-xs font-black uppercase tracking-[0.2em] mb-4">
            {new Date().toLocaleDateString('no-NO', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <h1 className="text-3xl md:text-5xl font-black text-white tracking-tight leading-tight mb-8">
            Hva skal du oppnå i dag, {currentUser?.name?.split(' ')[0] || 'Bruker'}? 🚀
          </h1>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => openActivityModal(null)} className="px-8 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:from-blue-800 hover:to-indigo-800 hover:scale-105 transition-all shadow-2xl shadow-blue-900/20 flex items-center gap-3 border border-blue-600">
              <SafeIcon icon="➕" className="text-blue-100" /> Ny Aktivitet
            </button>
            <button onClick={() => setIsMeetingModalOpen(true)} className="px-8 py-4 bg-white text-slate-900 border border-slate-200 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 hover:scale-105 transition-all shadow-xl shadow-slate-200/50 flex items-center gap-3">
              <SafeIcon icon="🎥" className="text-red-500" /> Book Møte
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 -mt-24 relative z-20 space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden flex flex-col min-h-[500px]">
            <div className="p-5 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <SafeIcon icon="🕒" className="text-blue-100" />
                <h3 className="font-black uppercase tracking-widest text-xs">Agenda</h3>
              </div>
              <span className="bg-white/10 px-2 py-1 rounded text-[10px] font-bold">
                {activityBuckets.todays.length + activityBuckets.tomorrows.length} Hendelser
              </span>
            </div>
            <div className="p-6 overflow-y-auto flex-1 custom-scrollbar relative">
              <div className="absolute left-[29px] top-6 bottom-6 w-0.5 bg-slate-100"></div>
              <div className="mb-6 relative">
                <h4 className="text-xs font-black text-primary-600 uppercase tracking-widest mb-2 pl-8">I dag</h4>
                {activityBuckets.todays.length > 0 ? activityBuckets.todays.map(renderTimelineItem) : renderEmptyState("Ingenting planlagt i dag.")}
              </div>
              <div className="mb-6 relative">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-8">I morgen</h4>
                {activityBuckets.tomorrows.length > 0 ? activityBuckets.tomorrows.map(renderTimelineItem) : renderEmptyState("Ingenting planlagt i morgen.")}
              </div>
              <div className="relative">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 pl-8">Senere</h4>
                {activityBuckets.upcoming.slice(0, 3).map(renderTimelineItem)}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden flex flex-col min-h-[500px]">
            <div className="p-5 bg-white border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <SafeIcon icon="⚠️" className="text-amber-500" />
                <h3 className="font-black text-slate-900 uppercase tracking-widest text-xs">Fokus & Avvik</h3>
              </div>
            </div>
            <div className="flex-1 p-6 flex flex-col items-center justify-center text-center">
              {activityBuckets.overdue.length > 0 ? (
                <div className="w-full space-y-4">
                  <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <SafeIcon icon="⏰" className="w-10 h-10 text-amber-500" />
                  </div>
                  <h3 className="text-lg font-black text-slate-900">Du har ting som haster!</h3>
                  <p className="text-slate-500 text-sm mb-6 max-w-xs mx-auto">
                    {activityBuckets.overdue.length} aktiviteter har forfalt. Ta tak i disse før du går videre.
                  </p>
                  <div className="space-y-2 w-full text-left">
                    {activityBuckets.overdue.slice(0, 3).map(act => (
                      <div key={getDashboardActivityKey(act)} className="p-3 bg-amber-50 border border-amber-100 rounded-xl flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                          <div className="min-w-0">
                            <span className="text-xs font-bold text-amber-900 block truncate">{getActivityDisplayTitle(act, companies, deals)}</span>
                          </div>
                        </div>
                        <span className="text-[10px] text-amber-700 font-medium">{formatDate(act.date)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <img src="https://cdn-icons-png.flaticon.com/512/7486/7486744.png" alt="Relaxing" className="w-32 h-32 mb-6 opacity-80" />
                  <h3 className="text-lg font-black text-slate-900">Alt er ajour</h3>
                  <p className="text-slate-500 text-sm max-w-xs mx-auto mt-2">
                    Ingen forfalte oppgaver eller kritiske avvik. Godt jobbet!
                  </p>
                  <div className="mt-8 p-4 bg-emerald-50 rounded-2xl border border-emerald-100 w-full">
                    <p className="text-xs font-bold text-emerald-700 uppercase tracking-widest flex items-center justify-center gap-2">
                      <SafeIcon icon="✅" /> System Status: God
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden flex flex-col min-h-[500px]">
            <div className="p-5 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <SafeIcon icon="✅" className="text-emerald-200" />
                <h3 className="font-black uppercase tracking-widest text-xs">Mine Gjøremål</h3>
              </div>
              <button onClick={() => openActivityModal(null)} className="text-[10px] bg-white/20 hover:bg-white/30 px-2 py-1 rounded font-bold transition-all">
                + Ny
              </button>
            </div>
            <div className="p-2 flex-1 overflow-y-auto custom-scrollbar">
              <div className="space-y-1">
                {[...activityBuckets.overdue, ...activityBuckets.todays, ...activityBuckets.upcoming].slice(0, 8).map((activity) => (
                  <MemoizedDashboardActivityCard
                    key={getDashboardActivityPlacementKey(activity, 'goals')}
                    activity={activity}
                    onOpenActivity={handleEditActivity}
                    onMarkDone={handleMarkDone}
                    companyName={getActivityCompanyName(activity, companies, deals)}
                    companies={companies}
                    deals={deals}
                    isOverdue={activityBuckets.overdue.includes(activity)}
                    variant="checklist"
                    placement="goals"
                  />
                ))}
                {[...activityBuckets.overdue, ...activityBuckets.todays, ...activityBuckets.upcoming].length === 0 && (
                  <div className="p-8 text-center text-slate-400">
                    <p className="text-sm">Ingen oppgaver i listen.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-[2rem] border border-slate-200 p-8 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-black text-slate-900">Salgspipeline</h3>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Øyeblikksbilde</p>
            </div>
            <div className="space-y-6 mb-8">
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-bold text-slate-600">
                  <span>Prospekt / Lead</span>
                  <span>{pipelineStats.count} stk</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                  <div className="bg-orange-500 h-full rounded-full" style={{ width: '60%' }}></div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-bold text-slate-600">
                  <span>Forhandling</span>
                  <span>{(deals || []).filter(d => isActiveDeal(d) && (d.stage_id?.includes('4') || d.stage_id?.includes('5'))).length} stk</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                  <div className="bg-blue-500 h-full rounded-full" style={{ width: '30%' }}></div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t border-slate-100">
              <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100">
                <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-1">Nye Muligheter</p>
                <p className="text-2xl font-black text-slate-900">{pipelineStats.generated}</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-1">Pipeline Verdi</p>
                <p className="text-2xl font-black text-slate-900">{formatCurrency(pipelineStats.value, true)}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-2xl border border-purple-100">
                <p className="text-[10px] font-black text-purple-500 uppercase tracking-widest mb-1">Vunnet</p>
                <p className="text-2xl font-black text-slate-900">{formatCurrency(pipelineStats.wonValue, true)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[2rem] border border-slate-200 p-8 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-black text-slate-900">Dokumentoversikt</h3>
              <select className="bg-slate-50 border border-slate-200 rounded-lg text-[10px] font-bold px-2 py-1 outline-none">
                <option>NOK</option>
                <option>USD</option>
              </select>
            </div>
            <div className="space-y-4">
              <div className="p-5 bg-sky-100 rounded-xl flex justify-between items-center">
                <div>
                  <p className="text-xs font-bold text-sky-800 uppercase tracking-widest">Sendt</p>
                  <p className="text-2xl font-black text-sky-900 mt-1">
                    {(sentContracts || []).filter(c => c.status === 'sent').length} <span className="text-xs font-medium opacity-60">Dokumenter</span>
                  </p>
                </div>
                <p className="text-lg font-bold text-sky-900 opacity-80">{formatCurrency(documentStats.sentValue)}</p>
              </div>
              <div className="p-5 bg-amber-100 rounded-xl flex justify-between items-center">
                <div>
                  <p className="text-xs font-bold text-amber-800 uppercase tracking-widest">Åpnet / Vist</p>
                  <p className="text-2xl font-black text-amber-900 mt-1">
                    {(sentContracts || []).filter(c => c.status === 'viewed').length} <span className="text-xs font-medium opacity-60">Dokumenter</span>
                  </p>
                </div>
                <p className="text-lg font-bold text-amber-900 opacity-80">{formatCurrency(documentStats.viewedValue)}</p>
              </div>
              <div className="p-5 bg-emerald-100 rounded-xl flex justify-between items-center">
                <div>
                  <p className="text-xs font-bold text-emerald-800 uppercase tracking-widest">Signert</p>
                  <p className="text-2xl font-black text-slate-900 mt-1">
                    {(sentContracts || []).filter(c => c.status === 'signed').length} <span className="text-xs font-medium opacity-60">Dokumenter</span>
                  </p>
                </div>
                <p className="text-lg font-bold text-emerald-900 opacity-80">{formatCurrency(documentStats.signedValue)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-[2rem] border border-slate-200 p-8 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-black text-slate-900">Omsetningsoversikt</h3>
              <p className="text-xs text-slate-400">Siste 12 mnd</p>
            </div>
            <div className="space-y-3">
              <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-100 flex justify-between items-center">
                <span className="text-xs font-bold text-yellow-800 uppercase tracking-widest">Utestående</span>
                <span className="font-black text-yellow-900">{formatCurrency(0)}</span>
              </div>
              <div className="p-4 bg-rose-50 rounded-xl border border-rose-100 flex justify-between items-center">
                <span className="text-xs font-bold text-rose-800 uppercase tracking-widest">Forfalt</span>
                <span className="font-black text-rose-900">{formatCurrency(0)}</span>
              </div>
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100 flex justify-between items-center">
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-widest">Innbetalt</span>
                <span className="font-black text-emerald-900">{formatCurrency(pipelineStats.wonValue)}</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-[2rem] border border-slate-200 p-8 shadow-sm">
            <h3 className="text-lg font-black text-slate-900 mb-6">Kunder</h3>
            <div className="space-y-4">
              <div className="p-6 bg-indigo-50 border border-indigo-100 rounded-2xl">
                <p className="text-xs font-bold text-indigo-500 uppercase tracking-widest mb-1">Totalt antall kunder</p>
                <p className="text-4xl font-black text-indigo-900">{(companies || []).filter(c => c.status === 'Customer').length}</p>
              </div>
              <div className="p-6 bg-slate-50 border border-slate-100 rounded-2xl">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Aktive leads</p>
                <p className="text-4xl font-black text-slate-900">{(deals || []).filter(isActiveDeal).length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BookMeetingModal isOpen={isMeetingModalOpen} onClose={() => setIsMeetingModalOpen(false)} />
      <BaseModal
        isOpen={isActivityModalOpen}
        onClose={closeActivityModal}
        title={selectedActivity ? "Rediger Aktivitet" : "Ny Aktivitet"}
        disableAnimation={true}
      >
        <ActivityForm activity={selectedActivity} onClose={closeActivityModal} />
      </BaseModal>
    </div>
  );
};

export default Dashboard;